import { format } from 'date-fns'
import type { Baby, DoseDefinition, VaccinationRecord } from '../types'
import { getEffectiveDate } from './vaccineDate'
import { applyMinIntervalFloors, getScheduleDependency, resolveCandidateDate } from './vaccineRules'
import { getExclusiveSkipRules, hasSpecialCompletionImpact, matchesSkipRule } from './vaccineCompletionRules'

function predecessorIds(dose: DoseDefinition, doses: DoseDefinition[]): string[] {
  const ids = new Set(dose.minIntervals?.map(rule => rule.fromDoseId) ?? [])
  const anchor = getScheduleDependency(dose)
  const source = anchor && doses.find(item => item.vaccineId === anchor.vaccineId && item.doseIndex === anchor.doseIndex)
  if (source) ids.add(source.id)
  return [...ids]
}

/** 沿日期依賴往後找；已完成的劑次有自己的實際日期，因此不再向下傳遞。 */
export function getScheduleDependentRecords(
  source: VaccinationRecord,
  records: VaccinationRecord[],
  doses: DoseDefinition[],
): VaccinationRecord[] {
  const siblings = records.filter(record => record.babyId === source.babyId)
  const seen = new Set([source.doseId])
  const queue = [source.doseId]
  const result: VaccinationRecord[] = []
  for (let i = 0; i < queue.length; i++) {
    for (const dose of doses) {
      if (seen.has(dose.id) || !predecessorIds(dose, doses).includes(queue[i]!)) continue
      const record = siblings.find(item => item.doseId === dose.id)
      if (!record || record.status === 'completed') continue
      seen.add(dose.id)
      queue.push(dose.id)
      result.push(record)
    }
  }
  return result
}

/**
 * 分開回報日期依賴與完成接種才會處理的相關項目，讓確認視窗使用對應文案。
 * 只列出尚未完成且未隱藏的紀錄；合併清單去重。
 */
export function getManualSkipImpacts(
  source: VaccinationRecord,
  records: VaccinationRecord[],
  doses: DoseDefinition[],
): { schedule: VaccinationRecord[]; related: VaccinationRecord[]; all: VaccinationRecord[] } {
  const schedule = getScheduleDependentRecords(source, records, doses)
    .filter(target => target.status !== 'skipped')
  const rules = getExclusiveSkipRules(source.doseId, doses.find(dose => dose.id === source.doseId))
  const related = records.filter(target => target.babyId === source.babyId && target.id !== source.id &&
    target.status !== 'completed' && target.status !== 'skipped' &&
    (rules.some(rule => matchesSkipRule(target, rule)) || hasSpecialCompletionImpact(source, target)))
  const all = [...new Map([...schedule, ...related].map(target => [target.id, target])).values()]
  return { schedule, related, all }
}

/**
 * 復原時重新算指定劑次及其下游。隱藏的前序也可能過舊，需遞迴算到最新基準；
 * 實際／自訂日期仍優先作為依賴基準，不覆寫使用者的日期或其他已完成紀錄。
 */
export function getRecalculatedPlannedDates(
  targets: VaccinationRecord[],
  records: VaccinationRecord[],
  doses: DoseDefinition[],
  baby: Baby,
): Map<string, string> {
  const siblings = records.filter(record => record.babyId === baby.id)
  const targetIds = new Set(targets.map(record => record.doseId))
  const planned = new Map<string, string>()
  const visiting = new Set<string>()
  const dates = new Map(siblings.map(record => [`${record.vaccineId}-${record.doseIndex}`, new Date(getEffectiveDate(record))]))
  const datesById = new Map(siblings.map(record => [record.doseId, new Date(getEffectiveDate(record))]))
  const birth = new Date(baby.birthDate)

  const resolve = (record: VaccinationRecord): Date => {
    const effective = () => new Date(record.actualDate ?? record.customPlannedDate ?? planned.get(record.doseId) ?? record.plannedDate)
    if (record.status === 'completed' || planned.has(record.doseId) || visiting.has(record.doseId)) return effective()
    if (!targetIds.has(record.doseId) && record.status !== 'skipped') return effective()
    const dose = doses.find(item => item.id === record.doseId)
    if (!dose) return effective()
    visiting.add(record.doseId)
    for (const id of predecessorIds(dose, doses)) {
      const previous = siblings.find(item => item.doseId === id)
      if (!previous) continue
      const date = resolve(previous)
      dates.set(`${previous.vaccineId}-${previous.doseIndex}`, date)
      datesById.set(previous.doseId, date)
    }
    const candidate = resolveCandidateDate(dose, {
      birthDate: birth, doseDates: dates, allDoses: doses,
      maternalRSVVaccinated: baby.maternalRSV?.vaccinated ?? false,
    })
    planned.set(record.doseId, candidate
      ? format(applyMinIntervalFloors(dose, candidate, birth, datesById), 'yyyy-MM-dd')
      : record.plannedDate)
    visiting.delete(record.doseId)
    return effective()
  }
  for (const target of targets) resolve(target)
  return planned
}
